import React from 'react'
import {
  X,
  TrendingUp,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Package,
  Eye,
  Activity,
  Calendar,
  Zap,
  Database,
  Check,
} from 'lucide-react'
import { Model } from '../../../../types/models/model'
import { Project } from '../../../../types/models/project'
import { useAnnotationStats } from '../../../../store/hooks'
import { ObjectDetectionHyperparameters } from '../../../../types/models/hyperparameters'
/* Using Bootstrap utilities */

interface Props {
  model: Model
  project: Project
  onClose: () => void
}

const Sidebar: React.FC<Props> = ({ model, project, onClose }) => {
  const trainDatasets = model.parameters?.train_metadata?.datasource || []
  const validationDatasets = model.parameters?.validation_metadata?.datasource || []

  const { data: trainStats } = useAnnotationStats(
    project?.id || '',
    project?.datasetid || '',
    trainDatasets,
    { skip: !project?.id || !project?.datasetid || trainDatasets.length === 0 }
  )

  const { data: validationStats } = useAnnotationStats(
    project?.id || '',
    project?.datasetid || '',
    validationDatasets,
    { skip: !project?.id || !project?.datasetid || validationDatasets.length === 0 }
  )

  const getModelStatus = (m: Model) =>
    m.state === 'TRAINING'
      ? 'training'
      : m.state === 'TRAINED'
      ? 'trained'
      : m.state === 'ERR'
      ? 'error'
      : 'unknown'

  const performanceMetrics = (() => {
    const isClassification =
      project?.annotation_type === 'classification' ||
      model.parameters?.hyperparameters?.type === 'classification'

    if (isClassification) {
      const trainingStats = model.training_stats?.classification?.training_job_metrics
      let bestEpoch = 'N/A'
      let bestAccuracy = 0
      if (trainingStats) {
        const jobKeys = Object.keys(trainingStats)
        if (jobKeys.length > 0) {
          const entries = trainingStats[jobKeys[0]]?.training_job_entries || []
          entries.forEach((entry: any) => {
            if (entry.accuracy > bestAccuracy) {
              bestAccuracy = entry.accuracy
              bestEpoch = entry.epoch
            }
          })
        }
      }
      return [
        {
          label: 'Train Accuracy',
          value: model.metrics?.['train:accuracy']
            ? `${((model.metrics['train:accuracy'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: TrendingUp,
          description: 'Final training accuracy achieved',
        },
        {
          label: 'Validation Accuracy',
          value: model.metrics?.['validation:accuracy']
            ? `${((model.metrics['validation:accuracy'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: BarChart3,
          description: 'Final validation accuracy achieved',
        },
        {
          label: 'Best Epoch',
          value: bestEpoch.toString(),
          icon: ArrowUp,
          description: 'Epoch that achieved the highest accuracy',
        },
        {
          label: 'Best Accuracy',
          value: bestAccuracy > 0 ? `${(bestAccuracy * 100).toFixed(1)}%` : 'N/A',
          icon: Check,
          description: 'Highest accuracy achieved during training',
        },
      ]
    }
    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      return [
        {
          label: 'mAP@50',
          value: model.metrics?.['validation:mAP50']
            ? `${((model.metrics['validation:mAP50'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: TrendingUp,
          description: 'Mean Average Precision at IoU threshold 0.5',
        },
        {
          label: 'mAP@50-95',
          value: model.metrics?.['validation:mAP50-95']
            ? `${((model.metrics['validation:mAP50-95'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: BarChart3,
          description: 'Mean Average Precision averaged across IoU thresholds 0.5-0.95',
        },
        {
          label: 'Precision',
          value: model.metrics?.['validation:precision']
            ? `${((model.metrics['validation:precision'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: ArrowUp,
          description: 'Ratio of true positive predictions to total positive predictions',
        },
        {
          label: 'Recall',
          value: model.metrics?.['validation:recall']
            ? `${((model.metrics['validation:recall'] as number) * 100).toFixed(1)}%`
            : 'N/A',
          icon: ArrowDown,
          description: 'Ratio of true positive predictions to total actual positives',
        },
      ]
    }
    return []
  })()

  const datasetInfo = (() => {
    const trainClassNames = trainStats?.statistics?.annotations_per_class
      ? Object.values(trainStats.statistics.annotations_per_class)
          .map((c: any) => c.name)
          .filter((n) => n !== 'Unknown' && n !== 'null')
      : []
    const validationClassNames = validationStats?.statistics?.annotations_per_class
      ? Object.values(validationStats.statistics.annotations_per_class)
          .map((c: any) => c.name)
          .filter((n) => n !== 'Unknown' && n !== 'null')
      : []
    const allClassNames = [...new Set([...trainClassNames, ...validationClassNames])]
    const trainImageCount =
      (trainStats?.statistics?.total_annotated_images || 0) +
      (trainStats?.statistics?.total_unannotated_images || 0)
    const validationImageCount =
      (validationStats?.statistics?.total_annotated_images || 0) +
      (validationStats?.statistics?.total_unannotated_images || 0)
    const hp = model.parameters?.hyperparameters
    const inner = hp?.hyperparameters
    const epochsVal = (inner?.training?.epochs?.value as number) || undefined

    let imageSizeVal: number | undefined = undefined
    let augmentationVal = false

    if (hp?.type === 'object_detection') {
      const odTraining = (inner as ObjectDetectionHyperparameters | null | undefined)?.training
      imageSizeVal = odTraining?.image_shape?.value as number | undefined
      augmentationVal = (odTraining?.augment?.value as boolean | undefined) ?? false
    }

    return {
      training: trainDatasets,
      validation: validationDatasets,
      trainImageCount,
      validationImageCount,
      trainAnnotations: trainStats?.statistics?.total_annotations || 0,
      validationAnnotations: validationStats?.statistics?.total_annotations || 0,
      classNames: allClassNames,
      totalClasses: allClassNames.length,
      epochs: epochsVal ?? 'N/A',
      imageSize: imageSizeVal ?? 'N/A',
      augmentation: augmentationVal,
      trainDatasetStats: trainStats?.statistics,
      validationDatasetStats: validationStats?.statistics,
    }
  })()

  return (
    <div
      className="bg-dark border-start border-secondary d-flex flex-column position-relative"
      style={{ width: '55%' }}
    >
      <div
        className="bg-gradient-primary text-white d-flex align-items-center justify-content-between position-sticky top-0 z-3 shadow"
        style={{ padding: '1.25rem 2rem' }}
      >
        <div className="d-flex flex-column gap-1">
          <div className="fs-4 fw-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,.3)' }}>
            {model.name}
          </div>
          <div className="d-flex align-items-center gap-3 opacity-75">
            <div className="d-inline-flex me-2">
              <span
                className="text-white d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                style={{
                  background:
                    model.state === 'TRAINED'
                      ? 'linear-gradient(135deg,#10b981,#059669)'
                      : model.state === 'TRAINING'
                      ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                      : model.state === 'ERR'
                      ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                      : 'linear-gradient(135deg,#6b7280,#4b5563)',
                }}
              >
                {model.state}
              </span>
            </div>
            <span className="small">{!model.parent_id ? 'Foundation Model' : 'Branch Model'}</span>
            <span className="small" style={{ opacity: 0.8 }}>
              {datasetInfo.totalClasses} classes
            </span>
          </div>
        </div>
        <button
          className="btn btn-light bg-opacity-25 border-0 fw-semibold d-flex align-items-center gap-2"
          onClick={onClose}
        >
          <X size={16} />
          Close
        </button>
      </div>

      <div
        className="flex-grow-1 overflow-auto"
        style={{ background: 'linear-gradient(180deg,#1a1a1a,#1e1e1e)' }}
      >
        <div className="p-3 p-md-4">
          {model.parent_id && (
            <div
              style={{
                fontSize: '0.85rem',
                color: '#b0b0b0',
                fontFamily: 'Monaco, Menlo, monospace',
                textAlign: 'center',
                padding: '0.75rem',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid #404040',
              }}
            >
              <div
                style={{
                  marginBottom: '0.25rem',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Based on
              </div>
              {model.parent_id}
            </div>
          )}

          {datasetInfo.classNames.length > 0 && (
            <div
              className="rounded-4 overflow-hidden border border-secondary mb-4"
              style={{ background: 'rgba(42,42,42,.6)', backdropFilter: 'blur(10px)' }}
            >
              <div
                className="d-flex align-items-center gap-2 fw-semibold text-white border-bottom border-secondary px-4 py-3"
                style={{ background: 'linear-gradient(135deg,#333,#404040)' }}
              >
                <Eye size={18} />
                What This Model Detects
              </div>
              <div className="p-4">
                <div
                  className="rounded-4 border p-3"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(102,126,234,.05), rgba(118,75,162,.05))',
                    borderColor: '#667eea20',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2 fw-semibold text-white">
                      <Package size={16} />
                      Detection Classes
                    </div>
                    <div className="badge bg-gradient-primary">{datasetInfo.totalClasses}</div>
                  </div>
                  <div
                    className="d-flex flex-wrap gap-2 p-2 rounded border"
                    style={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      background: 'rgba(0,0,0,.2)',
                      borderColor: '#333',
                    }}
                  >
                    {datasetInfo.classNames.map((className: string, index: number) => (
                      <div
                        key={index}
                        className="rounded-pill border px-2 py-1"
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(102,126,234,.3), rgba(118,74,162,.3))',
                          color: '#fff',
                          borderColor: '#667eea40',
                        }}
                      >
                        {className}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className="rounded-4 overflow-hidden border border-secondary mb-4"
            style={{ background: 'rgba(42,42,42,.6)', backdropFilter: 'blur(10px)' }}
          >
            <div
              className="d-flex align-items-center gap-2 fw-semibold text-white border-bottom border-secondary px-4 py-3"
              style={{ background: 'linear-gradient(135deg,#333,#404040)' }}
            >
              <Database size={18} />
              Training Data
            </div>
            <div className="p-4">
              <div
                className="rounded-4 border p-3 mb-3"
                style={{
                  background: 'linear-gradient(135deg, rgba(102,126,234,.1), rgba(118,75,162,.1))',
                  borderColor: '#667eea40',
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-circle bg-gradient-primary"
                    style={{ width: 12, height: 12 }}
                  />
                  <div className="fw-semibold">Training Dataset</div>
                </div>
                <div
                  style={{
                    fontSize: '0.85rem',
                    color: '#b0b0b0',
                    marginBottom: '1rem',
                    lineHeight: 1.4,
                  }}
                >
                  Sources:{' '}
                  {datasetInfo.training.length > 0 ? datasetInfo.training.join(', ') : 'N/A'}
                </div>
                <div
                  className="d-grid"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  <div className="text-center">
                    <div className="fw-bold text-white" style={{ fontSize: '1.5rem' }}>
                      {datasetInfo.trainImageCount.toLocaleString()}
                    </div>
                    <div className="text-secondary small fw-medium">Images</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-white" style={{ fontSize: '1.5rem' }}>
                      {datasetInfo.trainAnnotations.toLocaleString()}
                    </div>
                    <div className="text-secondary small fw-medium">Annotations</div>
                  </div>
                </div>
              </div>

              {datasetInfo.validation.length > 0 && (
                <div
                  className="rounded-4 border p-3"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16,185,129,.1), rgba(5,150,105,.1))',
                    borderColor: '#10b98140',
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="rounded-circle bg-gradient-success"
                      style={{ width: 12, height: 12 }}
                    />
                    <div className="fw-semibold">Validation Dataset</div>
                  </div>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#b0b0b0',
                      marginBottom: '1rem',
                      lineHeight: 1.4,
                    }}
                  >
                    Sources: {datasetInfo.validation.join(', ')}
                  </div>
                  <div
                    className="d-grid"
                    style={{
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '1rem',
                    }}
                  >
                    <div className="text-center">
                      <div className="fw-bold text-white" style={{ fontSize: '1.5rem' }}>
                        {datasetInfo.validationImageCount.toLocaleString()}
                      </div>
                      <div className="text-secondary small fw-medium">Images</div>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold text-white" style={{ fontSize: '1.5rem' }}>
                        {datasetInfo.validationAnnotations.toLocaleString()}
                      </div>
                      <div className="text-secondary small fw-medium">Annotations</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {performanceMetrics.length > 0 && (
            <div
              className="rounded-4 overflow-hidden border border-secondary mb-4"
              style={{ background: 'rgba(42,42,42,.6)', backdropFilter: 'blur(10px)' }}
            >
              <div
                className="d-flex align-items-center gap-2 fw-semibold text-white border-bottom border-secondary px-4 py-3"
                style={{ background: 'linear-gradient(135deg,#333,#404040)' }}
              >
                <TrendingUp size={18} />
                Performance Metrics
              </div>
              <div className="p-4">
                <div
                  className="d-grid"
                  style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}
                >
                  {performanceMetrics.map((metric, index) => (
                    <div
                      key={index}
                      className="rounded-4 border text-center p-3"
                      style={{
                        background: 'linear-gradient(135deg,#333,#404040)',
                        borderColor: '#505050',
                      }}
                      title={(metric as any).description}
                    >
                      <div
                        className="rounded-3 mx-auto mb-2 d-flex align-items-center justify-content-center"
                        style={{
                          width: 32,
                          height: 32,
                          background:
                            'linear-gradient(135deg, rgba(102,126,234,.3), rgba(118,74,162,.3))',
                          color: '#667eea',
                        }}
                      >
                        <metric.icon size={16} />
                      </div>
                      <div className="fw-bold text-white">{(metric as any).value}</div>
                      <div className="text-secondary small fw-medium">{(metric as any).label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div
            className="rounded-4 overflow-hidden border border-secondary mb-4"
            style={{ background: 'rgba(42,42,42,.6)', backdropFilter: 'blur(10px)' }}
          >
            <div
              className="d-flex align-items-center gap-2 fw-semibold text-white border-bottom border-secondary px-4 py-3"
              style={{ background: 'linear-gradient(135deg,#333,#404040)' }}
            >
              <Activity size={18} />
              Deployment Status
            </div>
            <div className="p-4">
              <div
                style={{
                  background:
                    'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.1) 100%)',
                  border: '1px solid #10b98140',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1rem',
                  textAlign: 'center',
                }}
              >
                <span
                  className="text-white d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                  style={{
                    background: (() => {
                      const s = model.deployment?.status || 'READY'
                      switch (s) {
                        case 'IN_SERVICE':
                          return 'linear-gradient(135deg,#10b981,#059669)'
                        case 'CREATING':
                          return 'linear-gradient(135deg,#f59e0b,#d97706)'
                        case 'ERR':
                          return 'linear-gradient(135deg,#ef4444,#dc2626)'
                        default:
                          return 'linear-gradient(135deg,#6b7280,#4b5563)'
                      }
                    })(),
                  }}
                >
                  <Activity size={12} />
                  {model.deployment?.status === 'IN_SERVICE'
                    ? 'Ready to Use'
                    : model.deployment?.status || 'Ready'}
                </span>
                <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#b0b0b0' }}>
                  This model is{' '}
                  {model.deployment?.status === 'IN_SERVICE'
                    ? 'deployed and ready for inference'
                    : 'not currently in service'}
                </div>
              </div>
              <div
                className="d-grid"
                style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}
              >
                <div
                  className="text-center rounded border p-3"
                  style={{ background: 'rgba(255,255,255,.05)', borderColor: '#404040' }}
                >
                  <div className="text-secondary small fw-medium mb-2">Total Uses</div>
                  <div className="fw-bold text-white">
                    {model.deployment?.total_invocations?.toLocaleString() || '0'}
                  </div>
                </div>
                <div
                  className="text-center rounded border p-3"
                  style={{ background: 'rgba(255,255,255,.05)', borderColor: '#404040' }}
                >
                  <div className="text-secondary small fw-medium mb-2">Last Used</div>
                  <div className="fw-bold text-white">
                    {model.deployment?.last_invocation &&
                    model.deployment.last_invocation !== '0001-01-01T00:00:00Z'
                      ? new Date(model.deployment.last_invocation).toLocaleDateString()
                      : 'Never'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-4 overflow-hidden border border-secondary mb-4"
            style={{ background: 'rgba(42,42,42,.6)', backdropFilter: 'blur(10px)' }}
          >
            <div
              className="d-flex align-items-center gap-2 fw-semibold text-white border-bottom border-secondary px-4 py-3"
              style={{ background: 'linear-gradient(135deg,#333,#404040)' }}
            >
              <Calendar size={18} />
              Model Configuration
            </div>
            <div className="p-4">
              <div
                className="d-grid"
                style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}
              >
                <div
                  className="text-center rounded border p-3"
                  style={{ background: 'rgba(255,255,255,.05)', borderColor: '#404040' }}
                >
                  <div className="text-secondary small fw-medium mb-2">Input Size</div>
                  <div className="fw-bold text-white">{datasetInfo.imageSize}px</div>
                </div>
                <div
                  className="text-center rounded border p-3"
                  style={{ background: 'rgba(255,255,255,.05)', borderColor: '#404040' }}
                >
                  <div className="text-secondary small fw-medium mb-2">Epochs Trained</div>
                  <div className="fw-bold text-white">{datasetInfo.epochs}</div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Zap
                    size={16}
                    style={{ color: datasetInfo.augmentation ? '#10b981' : '#6b7280' }}
                  />
                  <span style={{ color: '#b0b0b0', fontSize: '0.9rem' }}>Data Augmentation:</span>
                  <span
                    style={{
                      color: datasetInfo.augmentation ? '#10b981' : '#6b7280',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {datasetInfo.augmentation ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Sidebar
